import {
  type UIMessage,
  isTextUIPart,
  isToolUIPart,
  isReasoningUIPart,
  getToolName,
} from "ai"
import { useAppSelector } from "~/ui-rtk/app/hooks"
import { DataResultView } from "./DataResultView"
import { MarkdownContent } from "./MarkdownContent"
import { getPersonaIcon } from "./personaConfig"
import { ToolInvocationIndicator } from "./ToolInvocationIndicator"

function PersonaAvatar({ persona }: { persona: string }) {
  const icon = getPersonaIcon(persona)
  return (
    <div
      className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
      style={{ backgroundColor: `${icon.color}20` }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke={icon.color}
        className="w-4 h-4"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d={icon.path} />
      </svg>
    </div>
  )
}

interface MessageBubbleProps {
  message: UIMessage
}

export function MessageBubble({ message }: MessageBubbleProps) {
  const isUser = message.role === "user"
  const persona = useAppSelector(state => state.chatbotSlice.selectedPersona)

  return (
    <div className={`chat ${isUser ? "chat-end" : "chat-start"}`}>
      {/* Avatar */}
      {!isUser && (
        <div className="chat-image">
          <PersonaAvatar persona={persona} />
        </div>
      )}
      {isUser && (
        <div className="chat-image">
          <div className="w-7 h-7 rounded-full bg-[#A57CFB]/20 flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="#A57CFB"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
              />
            </svg>
          </div>
        </div>
      )}

      <div
        className={`chat-bubble max-w-[85%] ${
          isUser
            ? "bg-system-purple-600 text-gray-100"
            : "bg-system-blue-700 text-gray-100"
        }`}
      >
        {message.parts.map((part, i) => {
          if (isReasoningUIPart(part)) {
            // Only show during active thinking; hide once reasoning is complete
            if (part.state !== "streaming") return null
            return (
              <div
                key={`reasoning-${i}`}
                className="flex items-center gap-2 text-sm opacity-70 py-1"
              >
                <span className="loading loading-dots loading-xs" />
                <span>Analyzing...</span>
              </div>
            )
          }
          if (isTextUIPart(part)) {
            if (isUser) {
              return (
                <div
                  key={`text-${i}`}
                  className="whitespace-pre-wrap text-base leading-relaxed"
                >
                  {part.text}
                </div>
              )
            }
            return (
              <MarkdownContent key={`text-${i}`}>{part.text}</MarkdownContent>
            )
          }
          if (isToolUIPart(part)) {
            const toolName = getToolName(part)
            if (
              part.state === "input-streaming" ||
              part.state === "input-available"
            ) {
              return (
                <ToolInvocationIndicator
                  key={`tool-${i}`}
                  toolName={toolName}
                />
              )
            }
            if (part.state === "output-available" && part.output) {
              const result = part.output as {
                success?: boolean
                data?: Array<Record<string, string | number>>
                rowCount?: number
                error?: string
              }
              if (result.success && result.data && result.data.length > 0) {
                return <DataResultView key={`result-${i}`} data={result.data} />
              }
              if (
                result.success &&
                (!result.data || result.data.length === 0)
              ) {
                return (
                  <p
                    key={`empty-${i}`}
                    className="text-xs opacity-50 italic py-0.5"
                  >
                    No results for this query
                  </p>
                )
              }
              if (!result.success && result.error) {
                return (
                  <p key={`error-${i}`} className="text-sm text-error italic">
                    Query failed: {result.error}
                  </p>
                )
              }
            }
            if (part.state === "output-error" && "errorText" in part) {
              return (
                <p key={`error-${i}`} className="text-sm text-error italic">
                  Query failed: {String(part.errorText)}
                </p>
              )
            }
          }
          return null
        })}
      </div>
    </div>
  )
}
